import React, { useEffect, useState } from "react";
import { api, formatCurrency, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from "recharts";
import { useAuth } from "@/contexts/AuthContext";
import { TrendingUp, Ticket, Users, Ban, ShoppingCart, Wallet, Clock, Globe, Store, Smartphone } from "lucide-react";
import { toast } from "sonner";

const COLORS = ["#F97316", "#3B82F6", "#10B981", "#8B5CF6", "#EF4444", "#F59E0B"];

export default function Dashboard() {
  const { company } = useAuth();
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const currency = company?.currency || "FCFA";

  useEffect(() => {
    setLoading(true);
    api.get(`/dashboard?period=${period}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [period]);

  if (loading || !data) return <div className="text-slate-500">Chargement…</div>;

  const stats = [
    { label: "Revenus", value: formatCurrency(data.revenue, currency), icon: TrendingUp, color: "orange" },
    { label: "Billets vendus", value: data.tickets_sold, icon: Ticket, color: "blue" },
    { label: "Clients", value: data.customers, icon: Users, color: "emerald" },
    { label: "Réservations", value: data.reservations, icon: ShoppingCart, color: "purple" },
    { label: "Temps réel", value: data.realtime, icon: Clock, color: "indigo" },
    { label: "Annulations", value: data.cancellations, icon: Ban, color: "red" },
    { label: "Panier moyen", value: formatCurrency(data.avg_cart, currency), icon: Wallet, color: "orange" },
    { label: "En attente", value: data.pending, icon: Clock, color: "amber" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold">Tableau de bord</h1>
          <p className="text-sm text-slate-500">Vue d'ensemble de votre activité</p>
        </div>
        <Tabs value={period} onValueChange={setPeriod}>
          <TabsList data-testid="dashboard-period-tabs">
            <TabsTrigger value="today" data-testid="period-today">Aujourd'hui</TabsTrigger>
            <TabsTrigger value="7d" data-testid="period-7d">7 jours</TabsTrigger>
            <TabsTrigger value="30d" data-testid="period-30d">30 jours</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((s) => (
          <Card key={s.label} className="hover:shadow-md hover:-translate-y-0.5 transition-all">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{s.label}</div>
                <s.icon className="w-4 h-4 text-orange-500" />
              </div>
              <div className="stat-num mt-2" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>{s.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="font-display text-lg">Revenus journaliers</CardTitle></CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer>
              <LineChart data={data.daily_revenue}>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => formatCurrency(v, currency)} />
                <Line type="monotone" dataKey="revenue" stroke="#F97316" strokeWidth={3} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="font-display text-lg">Moyens de paiement</CardTitle></CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={data.payment_breakdown} dataKey="revenue" nameKey="method" innerRadius={50} outerRadius={90}>
                  {data.payment_breakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => formatCurrency(v, currency)} />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-2 space-y-1 text-xs">
              {data.payment_breakdown.map((p, i) => (
                <div key={p.method} className="flex justify-between">
                  <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }}></span> {p.method}</div>
                  <div>{formatCurrency(p.revenue, currency)}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="font-display text-lg">Meilleures destinations</CardTitle></CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer>
              <BarChart data={data.top_destinations}>
                <XAxis dataKey="route" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => formatCurrency(v, currency)} />
                <Bar dataKey="revenue" fill="#F97316" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="font-display text-lg">Ventes par canal</CardTitle></CardHeader>
          <CardContent>
            {(data.channel_breakdown || []).length === 0 && <div className="text-sm text-slate-500 py-8 text-center">Aucune vente</div>}
            <ul className="space-y-3">
              {(data.channel_breakdown || []).map((ch) => {
                const Icon = ch.channel === "public" ? Globe : ch.channel === "cash" ? Store : Smartphone;
                const bg = ch.channel === "public" ? "bg-emerald-500" : ch.channel === "cash" ? "bg-blue-500" : "bg-orange-500";
                const total = (data.channel_breakdown || []).reduce((s, x) => s + x.revenue, 0) || 1;
                const pct = Math.round((ch.revenue / total) * 100);
                return (
                  <li key={ch.channel} data-testid={`channel-${ch.channel}`} className="space-y-1.5">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg text-white grid place-items-center ${bg}`}><Icon className="w-4 h-4" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold">{ch.label}</div>
                        <div className="text-xs text-slate-500">{ch.count} billet(s)</div>
                      </div>
                      <div className="text-right">
                        <div className="font-display font-semibold text-sm">{formatCurrency(ch.revenue, currency)}</div>
                        <div className="text-xs text-slate-500">{pct}%</div>
                      </div>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                      <div className={`h-full ${bg}`} style={{ width: `${pct}%` }}></div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="font-display text-lg">Meilleurs clients</CardTitle></CardHeader>
          <CardContent>
            {data.top_customers.length === 0 && <div className="text-sm text-slate-500 py-8 text-center">Aucun client encore</div>}
            <ul className="divide-y divide-slate-200 dark:divide-slate-800">
              {data.top_customers.map((c) => (
                <li key={c.id} className="flex items-center justify-between py-3">
                  <div>
                    <div className="font-semibold text-sm">{c.firstname} {c.name}</div>
                    <div className="text-xs text-slate-500">{c.phone} · {c.trips} voyages</div>
                  </div>
                  <div className="font-display font-semibold">{formatCurrency(c.spent, currency)}</div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="font-display text-lg">Commandes récentes</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200 dark:border-slate-800">
              <tr><th className="text-left py-2">N°</th><th className="text-left">Trajet</th><th className="text-left">Client</th><th className="text-left">Date</th><th className="text-left">Statut</th><th className="text-right">Total</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {data.recent_orders.map((t) => (
                <tr key={t.id} data-testid={`recent-${t.id}`}>
                  <td className="py-2 font-mono text-xs">{t.ticket_number}</td>
                  <td>{t.from_city} → {t.to_city}</td>
                  <td>{t.customer_firstname} {t.customer_name}</td>
                  <td className="text-xs">{t.date}</td>
                  <td><Badge variant={t.status === "used" ? "default" : t.status === "cancelled" ? "destructive" : "secondary"}>{t.status}</Badge></td>
                  <td className="text-right font-semibold">{formatCurrency(t.total, currency)}</td>
                </tr>
              ))}
              {data.recent_orders.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-slate-500">Aucune commande récente</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
